import { useMutation } from "@apollo/client/react";
import { CloseOutlined, CopyOutlined } from "@ant-design/icons";
import { App, Form, Input, Modal } from "antd";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { DuplicateRecipeDocument } from "../../gql/generated/graphqlSchema.tsx";
import { routes } from "../../routes.ts";

type FormValues = {
  name: string;
};

export const DuplicateRecipeModal = ({
  open,
  onClose,
  recipeId,
  recipeName,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly recipeId: string;
  readonly recipeName: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();

  const [duplicateRecipe, { loading }] = useMutation(DuplicateRecipeDocument, {
    onCompleted: (data) => {
      void message.success(t("recipes.duplicatedDone"));
      onClose();
      void navigate(routes.recipeDetail.to(data.duplicateRecipe.id));
    },
  });

  const handleOk = () => {
    form.validateFields().then((values) => {
      void duplicateRecipe({ variables: { id: recipeId, input: { name: values.name } } });
    });
  };

  return (
    <Modal
      afterClose={() => form.resetFields()}
      afterOpenChange={(visible) => {
        if (visible) form.setFieldsValue({ name: `${recipeName} ${t("recipes.duplicateNameSuffix")}` });
      }}
      cancelButtonProps={{ icon: <CloseOutlined /> }}
      confirmLoading={loading}
      destroyOnHidden
      okButtonProps={{ icon: <CopyOutlined /> }}
      okText={t("recipes.duplicate")}
      onCancel={onClose}
      onOk={handleOk}
      open={open}
      title={t("recipes.duplicateTitle")}
    >
      <Form form={form} layout="vertical" onFinish={handleOk}>
        <Form.Item
          label={t("recipes.name")}
          name="name"
          rules={[{ required: true, message: t("recipes.validation.nameRequired") }]}
        >
          <Input autoFocus placeholder={t("recipes.namePlaceholder")} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
